import React, { memo, useEffect, useRef, useState } from "react";

import styles from "./TintPicker.module.css";
import * as Typed from "./TintPicker.typed";

import {
  Backdrop,
  CircularProgress,
  Input,
  Typography,
} from "@material-ui/core";
import FormatColorFillIcon from "@material-ui/icons/FormatColorFill";

import imageUrl from "../assets/images/flat.png";

import { Price } from "./Price";
import { ColorList } from "./ColorList";

import { getTintingCost } from "../api/getTIntingCost";
import { getColorCode } from "../api/getColorCode";

import { useDebounce } from "./utils/useDebounce";
import { rgbIsLight } from "./utils/rgbIsLight";
import { paintCanvas } from "./utils/paintCanvas";

import { useSnackbar } from "notistack";
import { saveAction } from "./utils/saveAction";
import { ShareButton } from "./ShareButton";

const TintPicker = memo(() => {
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string | null>(null);

  const [colorInfo, setColorInfo] = useState<Typed.ColorInfo | null>(null);
  const [colorBase, setColorBase] = useState<string | null>(null);
  const [colorsArr, setColorsArr] = useState<Typed.ColorsArray[] | null>(null);
  const [costPerLiter, setCostPerLiter] = useState<number | null>(null);

  const [rgbString, setRgbString] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const onChangeColor = async (color: Typed.Rgb, code: string, id: string) => {
    setIsLoading(true);
    setCostPerLiter(null);

    const { r, g, b } = color;
    const rgbString = `${r}, ${g}, ${b}`;
    setRgbString(rgbString);

    setColorInfo({
      color,
      code,
      id,
      isPrimaryLight: rgbIsLight(r, g, b),
    });

    try {
      const data = await getTintingCost(id);

      setColorBase(data.base);
      setCostPerLiter(data.costPerLiter);
    } catch (e) {
      enqueueSnackbar("Произошла ошибка при расчёте стоимости.", {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        autoHideDuration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    paintCanvas(canvasRef.current, colorInfo, rgbString, imageUrl);
  }, [colorInfo, rgbString]);

  const onMakeScreen = async () => {
    if (!canvasRef.current) return;

    saveAction(enqueueSnackbar, canvasRef.current);
  };

  const debouncedSearch = useDebounce(inputValue, 900);

  useEffect(() => {
    (async () => {
      if (debouncedSearch) {
        setIsLoading(true);

        setColorInfo(null);
        setColorBase(null);
        setCostPerLiter(null);
        setRgbString(null);

        try {
          const answer = await getColorCode(debouncedSearch);
          setColorsArr(
            answer.map((el) => {
              const { r, g, b } = el.rgb;

              return { ...el, isLight: rgbIsLight(r, g, b) };
            })
          );
        } catch (e) {
          enqueueSnackbar("Произошла ошибка при получении цветов.", {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            autoHideDuration: 1500,
          });
        }

        setIsLoading(false);
      }
    })();
  }, [debouncedSearch, enqueueSnackbar]);

  return (
    <>
      {isLoading ? (
        <Backdrop className={styles["backdrop"]} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}
      <div className={styles["wrapper"]}>
        <Typography gutterBottom align="center" variant="h5">
          Сервис расчета стоимости колеровки
        </Typography>
        <div className={styles["tint-container"]}>
          <div className={styles["left-container"]}>
            <div className={styles["search-container"]}>
              <Input
                autoFocus
                className={styles["input"]}
                placeholder="Введите код цвета"
                value={inputValue ?? ""}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
            {colorsArr && (
              <div className={styles["colors-container"]}>
                {colorsArr.length === 0 ? (
                  "Не найдено"
                ) : (
                  <ColorList
                    colorsArr={colorsArr}
                    onChangeColor={onChangeColor}
                  />
                )}
              </div>
            )}
            {colorBase && (
              <div className={styles["info-block"]}>
                <div className={styles["info-item-description"]}>
                  <FormatColorFillIcon className={styles["icon"]} />
                  <div>База краски для колеровки:</div>
                  <span className={styles["color-base"]}>{colorBase}</span>
                </div>
              </div>
            )}
            {!!costPerLiter && <Price costPerLiter={costPerLiter} />}
          </div>
          <div>
            <div
              className={styles["flat-img"]}
              style={
                rgbString
                  ? {
                      backgroundColor: `rgb(${rgbString})`,
                    }
                  : {}
              }
            >
              <canvas ref={canvasRef}></canvas>
              {colorInfo && <ShareButton onMakeScreen={onMakeScreen} />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export { TintPicker };
